import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { BREAKPOINTS } from '@hookdeck/theme';
import { OutpostTenant } from '../../../../../../../typings/outpost';
import Button from '../../../common/base/Button';
import Container from '../../../common/base/Container';
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import useViewportBreakpoint from '../../../hooks/useViewportBreakpoint';
import MetricDatePicker, { useDateRange } from '../../DashboardScene/Metrics/MetricDatePicker';
import MetricChart from '../Metrics/MetricChart';
import ChartInteractionGroup from '../../../common/Chart/ChartInteractionGroup';
const columns_for_breakpoints: Record<keyof typeof BREAKPOINTS, number> = {
  mobile: 1,
  tablet: 2,
  laptop: 2,
  desktop: 3
};
const CHART_CONFIGS = [{
  title: 'Events',
  chart_type: 'bar' as const,
  metric_type: 'count' as const,
  endpoint: 'events' as const,
  measures: ['count'],
  value_key: 'Events.count',
  time_key: 'Events.createdAt',
  tooltip_label: 'Events'
}, {
  title: 'Events rate',
  chart_type: 'line' as const,
  metric_type: 'rate' as const,
  endpoint: 'events' as const,
  measures: ['count'],
  value_key: 'Events.count',
  time_key: 'Events.createdAt',
  tooltip_label: 'Events'
}, {
  title: 'Events per topic',
  chart_type: 'bar' as const,
  metric_type: 'count' as const,
  endpoint: 'events' as const,
  measures: ['count'],
  dimensions: ['topic'],
  value_key: 'Events.count',
  time_key: 'Events.createdAt',
  dimension_key: 'Events.topic',
  tooltip_label: 'Events'
}, {
  title: 'Error rate per destination',
  chart_type: 'line' as const,
  metric_type: 'percent' as const,
  endpoint: 'attempts' as const,
  measures: ['error_rate'],
  dimensions: ['destination_id'],
  value_key: 'Attempts.error_rate',
  time_key: 'Attempts.createdAt',
  dimension_key: 'Attempts.destination_id',
  tooltip_label: 'Error rate'
}];
export const TenantMetrics: React.FC<{
  tenant: OutpostTenant;
}> = t0 => {
  const $ = _c(30);
  const {
    tenant
  } = t0;
  const breakpoint = useViewportBreakpoint();
  const [date_range, onDateRangeChanged] = useDateRange();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [refresh_key, setRefreshKey] = useState(t1);
  let t2;
  if ($[1] !== onDateRangeChanged) {
    t2 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[1] = onDateRangeChanged;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 8
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      justify: "space-between",
      align: "center"
    };
    t5 = {
      t: 5.5,
      b: 4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text as="h2" size="l" bold={true} m={0} flex={{
      align: "center"
    }}>Metrics</Text>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gap: 3
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== date_range || $[9] !== onDateRangeChanged) {
    t8 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
    $[8] = date_range;
    $[9] = onDateRangeChanged;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Button outline={true} icon="reload" onClick={() => setRefreshKey(Date.now())}>Reload</Button>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8) {
    t10 = <Div flex={t4} m={t5}>{t6}<Div flex={t7}>{t8}{t9}</Div></Div>;
    $[12] = t8;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = columns_for_breakpoints[breakpoint];
  let t12;
  if ($[14] !== t11) {
    t12 = {
      columns: t11,
      gap: 6
    };
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      b: 20
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== date_range || $[18] !== refresh_key || $[19] !== tenant) {
    t14 = CHART_CONFIGS.map(config => <MetricChart key={config.title} {...config} tenant_id={tenant.id} date_range={date_range} refresh_key={refresh_key} />);
    $[17] = date_range;
    $[18] = refresh_key;
    $[19] = tenant;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t12 || $[22] !== t14) {
    t15 = <Div grid={t12} m={t13}>{t14}</Div>;
    $[21] = t12;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t10 || $[25] !== t15) {
    t16 = <Container left={true} m={t3}>{t10}{t15}</Container>;
    $[24] = t10;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16 || $[28] !== t2) {
    t17 = <ChartInteractionGroup onTimeRangeSelected={t2}>{t16}</ChartInteractionGroup>;
    $[27] = t16;
    $[28] = t2;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  return t17;
};